import { Box, Text, Textarea, type TextareaProps } from '@chakra-ui/react';
import type React from 'react';
import { MdError } from 'react-icons/md';
import Show from '@/components/Molecules/Show';
import { colors } from '@/components/ui/theme';
import { Tooltip } from '@/components/ui/tooltip';

export interface FaceliftTextAreaProps extends TextareaProps {
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	sx?: React.CSSProperties;
	textareaKey?: string;
	bg?: string;
	color?: string;
	topLabel?: string;
	error?: string;
	required?: boolean;
}

const FaceliftTextArea: React.FC<FaceliftTextAreaProps> = ({
	onChange,
	sx,
	textareaKey,
	topLabel,
	required,
	bg = 'brand.input.bg',
	color = 'brand.input.text',
	error,
	size = 'md',
	minHeight = '20',
	resize = 'none',
	...rest
}) => {
	return (
		<Box my={1}>
			<Show when={!!topLabel}>
				<Box display="flex">
					<Text fontStyle="italic">{topLabel}</Text>
					<Text color="brand.errors">{required ? '*' : ''}</Text>
				</Box>
			</Show>

			<Tooltip
				showArrow
				contentProps={{ css: { '--tooltip-bg': 'colors.brand.errors', display: error ? 'block' : 'none' } }}
				color="brand.muted"
				open={!!error}
				content={error}
				positioning={{ placement: 'top-center' }}
			>
				<Box position="relative">
					<Textarea
						bg={{ _light: 'gray.50', _dark: 'gray.800' }}
						color={{ _light: 'gray.700', _dark: 'gray.100' }}
						_placeholder={{
							color: { _light: 'gray.300', _dark: 'gray.500' },
							fontSize: 'xs',
							fontWeight: 'normal',
							opacity: 0.8,
						}}
						_focus={{
							outline: 'none',
							borderBottomColor: 'brand.solid',
						}}
						key={textareaKey}
						onChange={onChange}
						minHeight={minHeight}
						resize={resize}
						outline={0}
						border="none"
						boxShadow="none"
						size={size}
						borderBottom="2px solid"
						borderImage={
							!error
								? 'linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%) 1'
								: ''
						}
						borderColor={!error ? '' : 'brand.errors'}
						borderRadius="sm"
						px={{ base: 3, md: 4 }}
						py={{ base: 2, md: 3 }}
						fontSize="sm"
						lineHeight="normal"
						transition="all"
						style={sx}
						{...rest}
					/>
					<Show when={!!error}>
						<Box
							position="absolute"
							right={2}
							top={2}
							display="flex"
							alignItems="center"
						>
							<MdError size="16" color={colors.errors} />
						</Box>
					</Show>
				</Box>
			</Tooltip>
		</Box>
	);
};

export default FaceliftTextArea;
